import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownMessageProps {
  text: string;
}

/**
 * Sprint 37, story-195. Assistant replies only (user turns stay plain `whitespace-pre-wrap`).
 * Raw HTML is dropped (`skipHtml`), images are not rendered, `javascript:` and other unsafe URL
 * schemes are stripped by react-markdown's default `urlTransform`, and links always open in a new
 * tab with `rel="noopener noreferrer"`.
 */
export const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ text }) => (
  <div className="assistant-markdown space-y-2 break-words" data-testid="assistant-markdown">
    <ReactMarkdown
      skipHtml
      remarkPlugins={[remarkGfm]}
      disallowedElements={['img']}
      components={{
        a: ({ node: _node, children, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" className="text-primary underline">
            {children}
          </a>
        ),
        h1: ({ node: _node, children, ...props }) => (
          <h3 {...props} className="text-base font-bold mt-2">
            {children}
          </h3>
        ),
        h2: ({ node: _node, children, ...props }) => (
          <h4 {...props} className="text-sm font-bold mt-2">
            {children}
          </h4>
        ),
        h3: ({ node: _node, children, ...props }) => (
          <h5 {...props} className="text-sm font-semibold mt-2">
            {children}
          </h5>
        ),
        ul: ({ node: _node, ...props }) => <ul {...props} className="list-disc pl-5 space-y-0.5" />,
        ol: ({ node: _node, ...props }) => <ol {...props} className="list-decimal pl-5 space-y-0.5" />,
        table: ({ node: _node, ...props }) => (
          <div className="overflow-x-auto">
            <table {...props} className="text-xs border-collapse" />
          </div>
        ),
        th: ({ node: _node, ...props }) => (
          <th {...props} className="border border-gray-200 px-2 py-1 text-left font-semibold bg-gray-50" />
        ),
        td: ({ node: _node, ...props }) => <td {...props} className="border border-gray-200 px-2 py-1" />,
        code: ({ node: _node, ...props }) => <code {...props} className="rounded bg-gray-100 px-1 text-[0.85em]" />,
      }}>
      {text}
    </ReactMarkdown>
  </div>
);

export default MarkdownMessage;
